import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import uuid from 'node-uuid';
import { get, head, map } from 'lodash';
import { paymentPropTypes } from '../../../states/payments/propTypes';
import { payeePropTypes } from '../../../sharedComponents/PayeeList/propTypes';
import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import pageContent from '../Payments/payment.content';
import { getPayees } from '../../../states/payees/selectors';
import { goToPage, pages, setBackPage } from '../../../states/page/actions';
import { getLastPayment, getPaymentCount, getToAccount } from '../../../states/payments/selectors';
import { getPaymentsList } from '../../../states/payments/getPaymentsList';
import { toggleBatch, toggleBatchIfEligible } from '../../../states/batch/actions';
import {
	addMultiplePayments,
	removePayment,
	updateReference,
	updateDate,
	updateToAccount,
	updateFromAccount,
} from '../../../states/payments/actions';
import { getReference, hasPreviousCopCheckPerformed, hasAllRequiredCopFields } from '../../../helpers/fieldHelpers';
import { formatAddPayeeDetails } from '../../../helpers/payeeHelpers';
import { clearSelectedPayment, setSelectedPaymentId } from '../../../states/payeeSelector/actions';
import PayeesPage from '../../../sharedComponents/PayeesPage/PayeesPage';
import {
	getSelectedPaymentId,
	getSelectedFromAccount,
	getFromAccountCanPayBenificiaries,
} from '../../../states/payeeSelector/selectors';
import { getFromAccounts } from '../../../states/accounts/selectors';
import { TYPE_FASTER_PAYMENTS, PAYMENTS_LIMIT } from '../../../config';
import { getPayeeById } from '../../../states/commonSelectors/selectors';
import { goToEditPayeePage } from '../../../states/api/fasterPaymentActions';
import { setSelectedPayee } from '../../../managePayee/states/action';
import { getPage } from '../../../states/page/selectors';
import { COPCheck } from '../../../sharedComponents/COPCheck/COPCheck';
import { getShowPayees } from '../../../states/accountPaging/selectors';
import { updateBeneficiary } from '../../../cop/states/actions';
import { getCopResult } from '../../../cop/states/selectors';
import { getSelectedPayee } from '../../../managePayee/states/selectors';

export const isPayeeBiller = (payees, id) =>
	get(
		payees.find((payee) => payee.id === id),
		'isBiller',
		false
	);

const mapState = (state) => ({
	payments: getPaymentsList(state),
	toPayees: getPayees(state),
	lastPayment: getLastPayment(state),
	paymentCount: getPaymentCount(state),
	toAccount: getToAccount(state),
	selectedPaymentId: getSelectedPaymentId(state),
	fromAccount: getSelectedFromAccount(state),
	canPayBeneficiaries: getFromAccountCanPayBenificiaries(state),
	fromAccounts: getFromAccounts(state),
	getPayeeById: (id) => getPayeeById(state, id),
	currentPage: getPage(state),
	showPayees: getShowPayees(state),
	selectedPayee: getSelectedPayee,
	copResult: getCopResult(state),
});

const mapDispatch = (dispatch, props) => ({
	toggleIsBatch: (isBatch) => dispatch(toggleBatch(isBatch)),
	addMultiplePayments: (payments) => dispatch(addMultiplePayments(payments)),
	selectToAccount: (paymentId, accountId, toPayees) => {
		dispatch(updateToAccount({ id: paymentId, accountId }));
		dispatch(updateReference({ id: paymentId, reference: getReference(toPayees, accountId) }));
		if (isPayeeBiller(toPayees, accountId)) {
			dispatch(updateDate({ id: paymentId, date: moment() }));
		}
	},
	selectPayment: (paymentId) => dispatch(setSelectedPaymentId(paymentId)),
	continueToPayments: () => dispatch(goToPage(pages.FasterPayments)),
	clearSelectedPayment: () => dispatch(clearSelectedPayment()),
	handleRemovePayment: (id) => dispatch(removePayment({ id })),
	updateFromAccount: ({ id, accountId }) => dispatch(updateFromAccount({ id, accountId })),
	toggleBatchIfEligible: () => dispatch(toggleBatchIfEligible(TYPE_FASTER_PAYMENTS)),
	goToEditPayeePage: (isCOPEdit) => dispatch(goToEditPayeePage(isCOPEdit)),
	setSelectedPayee: (payee) => dispatch(setSelectedPayee(payee)),
	setBackPage: (page) => dispatch(setBackPage(page)),
	updateBeneficiary: ({ sourceAccountId, payeeDetails, copResult }) =>
		dispatch(updateBeneficiary({ id: props.id, sourceAccountId, payeeDetails, copResult })),
});

class FPPayeePage extends Component {
	constructor(props) {
		super(props);

		this.handlePayeeListContinue = this.handlePayeeListContinue.bind(this);
		this.handleCOPSuccess = this.handleCOPSuccess.bind(this);
		this.handleCOPCancel = this.handleCOPCancel.bind(this);
		this.handleCOPEdit = this.handleCOPEdit.bind(this);
		this.handleKeepAsIs = this.handleKeepAsIs.bind(this);
		this.handleEditPayee = this.handleEditPayee.bind(this);

		this.state = { isCOPCheckRequired: false, payeeId: '' };
	}

	/**
	 *
	 * @todo in answering another 'todo' below, we might not need to call with a
	 * selectedPayeeId argument
	 */
	handleEditPayee(selectedPayeeId, isCOPEdit = false) {
		/**
		 * @todo if all of this is seperate redux actions firing (KW to confirm)
		 * we should really just dispatch one thunk action and handle it all in an appropriate reducer
		 * in order to keep business logic outside of our UI Views.
		 *
		 * Looking across payments-lib, I kind of feel like this principle has been forgotten.
		 */
		const { setSelectedPayee, goToEditPayeePage, currentPage, setBackPage } = this.props;
		/**
		 * Get the Payee Object from `state.payees` to use as the "selected" payee, which the EditPayee screen observes.
		 */
		setSelectedPayee(this.props.getPayeeById(selectedPayeeId));
		setBackPage(currentPage);
		goToEditPayeePage(isCOPEdit);
	}

	handlePayeeListContinue(selectedToPayees) {
		const {
			toPayees,
			addMultiplePayments,
			selectToAccount,
			selectedPaymentId,
			lastPayment,
			continueToPayments,
			clearSelectedPayment,
			fromAccount,
			toggleBatchIfEligible,
			getPayeeById,
			showPayees,
		} = this.props;

		if (selectedPaymentId) {
			selectToAccount(selectedPaymentId, head(selectedToPayees), toPayees);
		} else {
			const lastPaymentDate = get(lastPayment, 'date', moment());
			const multiplePayments = map(selectedToPayees, (toPayee) => ({
				id: uuid.v1(),
				fromAccount,
				toAccount: toPayee,
				reference: getReference(toPayees, toPayee),
				// The following isPayeeBiller check seems redundant as toPayees from props has filtered out billers via getPayees()
				date: isPayeeBiller(toPayees, toPayee) ? moment() : lastPaymentDate,
			}));

			addMultiplePayments(multiplePayments);
			toggleBatchIfEligible();
		}
		if (!showPayees) {
			return continueToPayments();
		}
		if (selectedToPayees.length === 1) {
			const payee = getPayeeById(selectedToPayees[0]);
			if (!hasPreviousCopCheckPerformed(payee)) {
				if (hasAllRequiredCopFields(payee)) {
					this.setState({ isCOPCheckRequired: true, payeeId: payee.id });
				} else {
					this.handleEditPayee(payee.id, true);
				}
				return;
			}
		}

		clearSelectedPayment();
		return continueToPayments();
	}

	handleKeepAsIs() {
		const { continueToPayments } = this.props;
		continueToPayments();
	}

	handleCOPSuccess() {
		const {
			copResult: currentResult,
			getPayeeById,
			payments,
			continueToPayments,
			selectPayment,
			updateBeneficiary,
			setSelectedPayee,
		} = this.props;
		const paymentId = get(payments, '[0].id');
		const targetPayeeId = get(payments, '[0].toAccount');
		const sourceAccountId = get(payments, '[0].fromAccount');
		const targetPayee = targetPayeeId ? getPayeeById(targetPayeeId) : {};
		let copResult = currentResult;
		if (!copResult) {
			copResult = {
				payeeName: targetPayee.cop_account_name,
				payeeType: targetPayee.cop_account_type,
				result: {
					cop_matching_outcome: null,
					cop_reason_code: null,
				},
			};
		}

		setSelectedPayee(targetPayee);

		const payeeDetails = formatAddPayeeDetails({ targetPayee, reference: get(payments, '[0].reference', '') });
		selectPayment(paymentId);

		new Promise((resolve) => resolve(updateBeneficiary({ sourceAccountId, payeeDetails, copResult }))).then(() =>
			continueToPayments()
		);
	}

	handleCOPCancel() {
		const {
			lastPayment: { id: paymentId },
			handleRemovePayment,
			selectedPaymentId: isPaymentBeingEdited,
			setSelectedPayee,
		} = this.props;
		if (isPaymentBeingEdited) {
			setSelectedPayee(null);
		} else {
			handleRemovePayment(paymentId);
		}
		this.setState({ isCOPCheckRequired: false });
	}

	handleCOPEdit(payeeId) {
		this.handleEditPayee(payeeId, true);
		this.setState({ isCOPCheckRequired: false });
	}

	render() {
		const {
			toPayees,
			content: { header },
			content,
			copCheckTriggered,
			paymentCount,
			toAccount,
			updateFromAccount,
			canPayBeneficiaries,
			fromAccounts,
			handleDisplayFullwidth,
			sessionCOPChecks,
		} = this.props;
		const { isCOPCheckRequired, payeeId } = this.state;

		if (isCOPCheckRequired) {
			return (
				<COPCheck
					copCheckTriggered={copCheckTriggered}
					handleComplete={this.handleCOPSuccess}
					handleCancel={this.handleCOPCancel}
					handleEdit={this.handleCOPEdit}
					handleKeepAsIs={this.handleKeepAsIs}
					payeeId={payeeId}
					sessionCOPChecks={sessionCOPChecks}
				/>
			);
		}

		return (
			<PayeesPage
				toPayees={toPayees}
				header={header}
				description=""
				content={content}
				paymentCount={paymentCount}
				toAccount={toAccount}
				onPayeeListContinue={this.handlePayeeListContinue}
				updateFromAccount={updateFromAccount}
				showAccounts
				canPayBeneficiaries={canPayBeneficiaries}
				fromAccounts={fromAccounts}
				paymentLimit={PAYMENTS_LIMIT}
				handleDisplayFullwidth={handleDisplayFullwidth}
			/>
		);
	}
}

FPPayeePage.propTypes = {
	content: PropTypes.shape({}).isRequired,
	payments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	lastPayment: PropTypes.shape(paymentPropTypes),
	toPayees: PropTypes.arrayOf(PropTypes.shape(payeePropTypes)).isRequired,
	continueToPayments: PropTypes.func.isRequired,
	copCheckTriggered: PropTypes.func,
	addMultiplePayments: PropTypes.func.isRequired,
	selectedPaymentId: PropTypes.string,
	selectToAccount: PropTypes.func.isRequired,
	clearSelectedPayment: PropTypes.func.isRequired,
	toggleBatchIfEligible: PropTypes.func.isRequired,
	paymentCount: PropTypes.number.isRequired,
	updateFromAccount: PropTypes.func.isRequired,
	selectPayment: PropTypes.func.isRequired,
	updateBeneficiary: PropTypes.func.isRequired,
	fromAccount: PropTypes.string,
	toAccount: PropTypes.string,
	canPayBeneficiaries: PropTypes.bool,
	fromAccounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	handleDisplayFullWidth: PropTypes.func,
	sessionCOPChecks: PropTypes.object,
	copResult: PropTypes.object,
};

export const connectWrapper = connect(mapState, mapDispatch)(FPPayeePage);
const contentWrapper = withContent({ content: pageContent });
export default withScrollTop(contentWrapper(connectWrapper));
